import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RequireRole } from '../components/RequireRole';
import { TextField, FieldError, inputClass } from '../components/fields';
import { getOwnProfile, updateOwnProfile, type OwnProfile } from '../lib/members';

type Load = 'loading' | 'ready' | 'no_profile' | 'error';
type Save = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Spec 004 — member profile self-service (SC-407/408/409/410). Loads the caller's own profile; join
 * date, role and consent are shown read-only. Editable: display name, pronouns, bio, notification
 * preference. Four states: loading → ready (form) → no_profile (accounts without a member row, e.g. the
 * bootstrap admin) → error. WCAG 2.1 AA (labels, aria-live, focus moved to the saved confirmation).
 */
function ProfileInner() {
  const [load, setLoad] = useState<Load>('loading');
  const [profile, setProfile] = useState<OwnProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [bio, setBio] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [save, setSave] = useState<Save>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const savedRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    let active = true;
    getOwnProfile().then((r) => {
      if (!active) return;
      if (r.kind === 'ok') {
        setProfile(r.data);
        setDisplayName(r.data.displayName);
        setPronouns(r.data.pronouns ?? '');
        setBio(r.data.bio ?? '');
        setOptIn(r.data.notificationOptIn);
        setLoad('ready');
      } else if (r.kind === 'not_found') {
        setLoad('no_profile');
      } else {
        setLoad('error');
      }
    });
    return () => {
      active = false;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (save === 'saving') return;
    setFieldErrors({});
    setGeneralError(null);
    setSave('saving');
    const r = await updateOwnProfile({
      displayName,
      pronouns: pronouns || undefined,
      bio: bio || undefined,
      notificationOptIn: optIn,
    });
    if (r.kind === 'ok') {
      setProfile(r.data);
      setSave('saved');
      queueMicrotask(() => savedRef.current?.focus());
      return;
    }
    if (r.kind === 'invalid') {
      setFieldErrors(r.errors);
      setSave('error');
      return;
    }
    setGeneralError('We couldn’t save your profile. Please try again.');
    setSave('error');
  }

  if (load === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-xl flex-col items-center px-6 py-24" aria-live="polite">
        <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
        <p className="mt-4 font-serif text-lg text-ink">Loading your profile…</p>
      </div>
    );
  }

  if (load === 'no_profile') {
    return (
      <div className="mx-auto w-full max-w-xl px-6 py-24 text-center">
        <h1 className="font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">No profile yet</h1>
        <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
          This account doesn’t have a member profile. Member profiles are created when the board approves
          an application.
        </p>
      </div>
    );
  }

  if (load === 'error' || !profile) {
    return (
      <div className="mx-auto w-full max-w-xl px-6 py-24">
        <p role="alert" className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t load your profile. Please refresh and try again.
        </p>
      </div>
    );
  }

  const joined = new Date(profile.joinedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="mx-auto w-full max-w-xl px-6 py-16 sm:py-20">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">Your membership</p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">Profile</h1>

      <dl className="mt-6 grid grid-cols-[auto,1fr] gap-x-6 gap-y-1 font-sans text-sm">
        <dt className="text-muted">Member since</dt>
        <dd className="text-ink">{joined}</dd>
        <dt className="text-muted">Role</dt>
        <dd className="text-ink capitalize">{profile.role}</dd>
        <dt className="text-muted">Consent recorded</dt>
        <dd className="text-ink">version {profile.consentVersion}</dd>
      </dl>

      {generalError && (
        <p role="alert" className="mt-6 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          {generalError}
        </p>
      )}

      {save === 'saved' && (
        <p
          ref={savedRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          className="mt-6 flex items-center gap-2 font-sans text-sm text-success"
        >
          <CheckCircle2 aria-hidden className="h-4 w-4" />
          Profile saved.
        </p>
      )}

      <form onSubmit={submit} noValidate className="mt-8 space-y-6 border-t border-border pt-8">
        <TextField
          id="displayName"
          label="Display name"
          required
          value={displayName}
          onChange={(v) => { setDisplayName(v); setSave('idle'); }}
          error={fieldErrors.displayName}
          disabled={save === 'saving'}
        />
        <TextField
          id="pronouns"
          label="Pronouns"
          hint="Optional. For example: she/her, they/them."
          value={pronouns}
          onChange={(v) => { setPronouns(v); setSave('idle'); }}
          error={fieldErrors.pronouns}
          disabled={save === 'saving'}
        />
        <div className="space-y-1.5">
          <label htmlFor="bio" className="block font-sans text-sm font-medium text-ink">Bio</label>
          <p id="bio-hint" className="font-sans text-xs text-muted">Optional. Up to 2000 characters.</p>
          <textarea
            id="bio"
            value={bio}
            onChange={(e) => { setBio(e.target.value); setSave('idle'); }}
            rows={5}
            maxLength={2000}
            disabled={save === 'saving'}
            aria-describedby={['bio-hint', fieldErrors.bio ? 'bio-error' : null].filter(Boolean).join(' ')}
            aria-invalid={fieldErrors.bio ? true : undefined}
            className={`${inputClass} resize-y`}
          />
          <FieldError id="bio-error" error={fieldErrors.bio} />
        </div>
        <label className="flex items-start gap-3 font-sans text-sm text-ink">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => { setOptIn(e.target.checked); setSave('idle'); }}
            disabled={save === 'saving'}
            className="mt-0.5 h-4 w-4 rounded border-border text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
          <span>
            Send me non-essential notifications (meeting reminders, digests).
            <span className="mt-0.5 block text-xs text-muted">Sign-in and other essential emails are always sent.</span>
          </span>
        </label>

        <div className="flex flex-wrap items-center gap-4 pt-2">
          <button
            type="submit"
            disabled={save === 'saving'}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-2.5 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
          >
            {save === 'saving' && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
          <Link to="/privacy" className="font-sans text-sm text-accent-deep underline underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
            Privacy &amp; your data
          </Link>
        </div>
      </form>
    </div>
  );
}

export default function Profile() {
  return <RequireRole min="member">{() => <ProfileInner />}</RequireRole>;
}
